import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import CrudeInfoTab from './components/CrudeInfoTab';
import EvaluationTab from './components/EvaluationTab';
import ImplementationTab, {
  toFirstOfMonth,
} from './components/ImplementationTab';
import {
  createDraft,
  updateDraft,
  uploadDraftDocument,
  deleteDraftDocument,
  submitDraft,
} from './components/registrationApi';
import {
  validateCrudeInfo,
  validateImplementation,
  mapBackendErrors,
} from './components/registrationValidation';
import {
  computeTotalYield,
  deriveSulfurClassName,
  deriveYieldClassName,
} from './components/classification';
import { formatForAPI } from '../../../utils/dateformatpickr';

// Crude Registration — Create wizard (3 tabs). Uses the staged "Draft Bertahap"
// flow (api-crude-registration.md §D1–D6): a draft is created on Tab-1 Next,
// documents are uploaded per-file on Tab 2/3, and the whole thing is submitted
// (into the Review→Approve ChangedRequest workflow) on Tab-3 Submit. Submitted
// crude stays invisible in the list until approved — the success toast +
// redirect is the intended end state.
const TAB_CONFIG = [
  { id: 1, icon: 'ri-flask-line', label: 'CRUDE INFO' },
  { id: 2, icon: 'ri-file-list-3-line', label: 'EVALUATION' },
  { id: 3, icon: 'ri-settings-3-line', label: 'IMPLEMENTATION' },
];

// Form-field keys rendered on Tab 1 — used to route a backend 400 field error
// (e.g. AssaYear) to an inline highlight and to decide whether to bounce the
// user back to Tab 1 to fix it.
const CRUDE_INFO_FIELDS = [
  'crudeName',
  'origin',
  'country',
  'codeG5',
  'productionRate',
  'parcelSize',
  'penyaluran',
  'assaYear',
  'lab',
  'api',
  'sulfur',
  'pourPoint',
  'tan',
  'asphaltene',
  'sg',
  'salt',
  'sw',
  'mercury',
  'gas',
  'lNaphta',
  'hNaphta',
  'kerosene',
  'lGasoil',
  'hGasoil',
  'residu',
  'totalYield',
];

// Maps the flat form state to the D1/D2 draft payload (top-level general +
// assay/yield + compositions). Numbers are coerced; blank optionals stay null.
const buildDraftPayload = form => {
  const num = v =>
    v === '' || v === null || v === undefined ? null : Number(v);
  const totalYield = computeTotalYield(form);
  // Draft flow takes these enums as string member names (see classification.js).
  // On create there's no historical value, so *Before mirrors the current yield.
  const yieldName = deriveYieldClassName(totalYield, form.gas, form.hNaphta);
  const sulfurName = deriveSulfurClassName(form.sulfur);
  return {
    crudeName: form.crudeName?.trim(),
    origin: form.origin,
    country: form.country?.trim(),
    description: form.description || null,
    productionRate: form.productionRate,
    parcelSize: form.parcelSize || null,
    penyaluran: form.penyaluran,
    codeG5: form.codeG5?.trim(),
    assaYear: num(form.assaYear),
    lab: form.lab,
    api: num(form.api),
    sulfur: num(form.sulfur),
    pourPoint: num(form.pourPoint),
    tan: num(form.tan),
    asphaltene: num(form.asphaltene),
    sg: num(form.sg),
    salt: num(form.salt),
    sw: num(form.sw),
    mercury: num(form.mercury),
    classificationYield: yieldName,
    classificationYieldBefore: yieldName,
    classificationSulfur: sulfurName,
    evaluationNote: form.evaluationNote || null,
    gas: num(form.gas),
    lNaphta: num(form.lNaphta),
    hNaphta: num(form.hNaphta),
    kerosene: num(form.kerosene),
    lGasoil: num(form.lGasoil),
    hGasoil: num(form.hGasoil),
    residu: num(form.residu),
    totalYield,
    masaBerlaku: form.masaBerlaku
      ? formatForAPI(toFirstOfMonth(form.masaBerlaku))
      : null,
    isLatUpdate: !!form.isLatUpdate,
    compositions: form.isBlending
      ? (form.compositions || []).map(c => ({
          crudeText: c.crudeText,
          composition: Number(c.composition),
        }))
      : [],
  };
};

// Only CDU rows with a fully-filled group are sent; each technical*/
// implementation* group is all-or-nothing (validated before submit).
const buildImplementations = form => {
  const num = v =>
    v === '' || v === null || v === undefined ? null : Number(v);
  const rows = Object.values(form.implementations || {});
  return rows
    .filter(
      r =>
        r.technicalStatus !== '' ||
        r.implementationStatus !== '' ||
        r.technicalRatio ||
        r.implementationRatio
    )
    .map(r => ({
      cduId: r.cduId,
      technicalStatus:
        r.technicalStatus === '' || r.technicalStatus === undefined
          ? null
          : r.technicalStatus === 'true',
      technicalRatio: num(r.technicalRatio),
      technicalLimit: num(r.technicalLimit),
      implementationStatus: num(r.implementationStatus),
      implementationRatio: num(r.implementationRatio),
      implementationLimit: num(r.implementationLimit),
    }));
};

const RegistrationCreateView = () => {
  const navigate = useNavigate();
  document.title = 'Crude Registration';

  const [activeTab, setActiveTab] = useState(1);
  const [form, setForm] = useState({
    isBlending: false,
    compositions: [],
    implementations: {},
  });
  const [errors, setErrors] = useState({});

  // Per-cell error flags for Tab 3, shaped { [cduId]: { <fieldKey>: true } } —
  // highlights the blank cells of a touched-but-incomplete implementation group.
  const [implErrors, setImplErrors] = useState({});

  // Draft ids from D1 (needed by every subsequent call).
  const [crudeId, setCrudeId] = useState(null);
  const [crudeDetailId, setCrudeDetailId] = useState(null);

  // Documents grouped by evaluationId (shared by Tab 2 & Tab 3). Each row has a
  // lifecycle `status`: 'pending' rows are staged locally (carry the raw `file`,
  // no BE call yet) and 'saved' rows have been flushed to the Draft staging
  // endpoint (D4) and carry a `pendingDocumentId`. Uploads are deferred and
  // batch-flushed at the tab boundary (Tab-2 Next / before Submit) to avoid a
  // per-file request — and add-then-delete of an un-flushed file costs nothing.
  const [documentsByEval, setDocumentsByEval] = useState({});

  const [savingDraft, setSavingDraft] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  // True while a batch flush (D4 uploads) is in flight — disables Next/Submit.
  const [flushing, setFlushing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const goTo = tab => setActiveTab(tab);

  const warnToast = msg =>
    toast.error(msg, {
      position: 'top-right',
      className: 'bg-warning text-black',
    });

  // Central handler for a rejected API call. When the backend returns a 400 with
  // a per-field `errors` map (attached as `validationErrors` by the crude API
  // helper), map it onto the Tab-1 fields for inline highlighting and bounce the
  // user back to Tab 1; otherwise fall back to the plain string message toast.
  // Returns true when it handled a field-level validation error.
  const handleApiError = (error, fallbackPrefix) => {
    const validationErrors = error?.validationErrors;
    if (validationErrors) {
      const { fields, messages } = mapBackendErrors(
        validationErrors,
        CRUDE_INFO_FIELDS
      );
      if (Object.keys(fields).length > 0) {
        setErrors(fields);
        goTo(1);
      }
      warnToast(messages[0] || String(error));
      return true;
    }
    toast.error(`${fallbackPrefix}: ${error}`);
    return false;
  };

  // Tab 1 Next — validate, then create (D1) or update (D2) the draft.
  const handleNextFromCrudeInfo = async () => {
    const { errors: errorList, fields } = validateCrudeInfo(form);
    setErrors(fields);
    if (errorList.length > 0) {
      warnToast(errorList[0]);
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildDraftPayload(form);
      if (!crudeId) {
        const res = await createDraft(payload);
        setCrudeId(res?.crudeId);
        setCrudeDetailId(res?.crudeDetailId);
      } else {
        await updateDraft({ ...payload, crudeId, crudeDetailId });
      }
      goTo(2);
    } catch (error) {
      handleApiError(error, 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Shared document add — stages the file locally as a 'pending' row (no BE call).
  // The actual upload (D4) happens later in flushPendingDocuments() at the tab
  // boundary. Always resolves synchronously so the modal closes immediately.
  const handleAddDocument = ({ file, evaluationId, refDocument, effDate }) => {
    if (!crudeDetailId) {
      toast.error('Please complete Crude Info first');
      return;
    }
    setDocumentsByEval(prev => ({
      ...prev,
      [evaluationId]: [
        ...(prev[evaluationId] || []),
        {
          localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          status: 'pending',
          file,
          fileName: file.name,
          refDocument,
          effDate,
        },
      ],
    }));
  };

  // Batch-flush every 'pending' document to the Draft staging endpoint (D4).
  // Uploads sequentially so a failure is unambiguously attributable. On success
  // each row is promoted in place to 'saved' (gets pendingDocumentId, drops the
  // raw file) — so a retry never re-uploads an already-saved row. On the first
  // failure it stops and returns false, leaving saved rows saved and the rest
  // pending; the caller aborts the transition and the user stays on the tab.
  const flushPendingDocuments = async () => {
    if (!crudeDetailId) {
      toast.error('Please complete Crude Info first');
      return false;
    }
    // Snapshot the pending rows (evaluationId + localId) to upload.
    const pending = [];
    Object.entries(documentsByEval).forEach(([evaluationId, rows]) => {
      (rows || []).forEach(row => {
        if (row.status === 'pending') pending.push({ evaluationId, row });
      });
    });
    if (pending.length === 0) return true;

    setFlushing(true);
    try {
      for (const { evaluationId, row } of pending) {
        try {
          const res = await uploadDraftDocument({
            file: row.file,
            crudeDetailId,
            evaluationId,
            refDocument: row.refDocument,
            effDate: row.effDate,
          });
          // Promote this row → 'saved' in place, keyed by its localId.
          setDocumentsByEval(prev => ({
            ...prev,
            [evaluationId]: (prev[evaluationId] || []).map(d =>
              d.localId === row.localId
                ? {
                    localId: d.localId,
                    status: 'saved',
                    pendingDocumentId: res?.pendingDocumentId,
                    fileName: d.fileName,
                    refDocument: d.refDocument,
                    effDate: d.effDate,
                  }
                : d
            ),
          }));
        } catch (error) {
          toast.error(`Failed to upload "${row.fileName}": ${error}`);
          return false;
        }
      }
      return true;
    } finally {
      setFlushing(false);
    }
  };

  // Shared document delete. Pending (un-flushed) rows are removed locally with no
  // BE call; saved rows call the Draft delete endpoint (D5) first.
  const handleDeleteDocument = async (evaluationId, row) => {
    if (row.status === 'pending') {
      setDocumentsByEval(prev => ({
        ...prev,
        [evaluationId]: (prev[evaluationId] || []).filter(
          d => d.localId !== row.localId
        ),
      }));
      return;
    }
    setSavingDoc(true);
    try {
      await deleteDraftDocument(row.pendingDocumentId);
      setDocumentsByEval(prev => ({
        ...prev,
        [evaluationId]: (prev[evaluationId] || []).filter(
          d => d.pendingDocumentId !== row.pendingDocumentId
        ),
      }));
      toast.success('Document removed');
    } catch (error) {
      toast.error(`Failed to remove document: ${error}`);
    } finally {
      setSavingDoc(false);
    }
  };

  // Tab 2 Next — flush staged Tab-2 files (D4) before advancing; on partial
  // failure the user stays on Tab 2 to retry.
  const handleNextFromEvaluation = async () => {
    if (await flushPendingDocuments()) goTo(3);
  };

  // Tab 3 Submit — validate implementations, then D6.
  const handleSubmit = async () => {
    const { errors: errorList, fields } = validateImplementation(form);
    setImplErrors(fields);
    if (errorList.length > 0) {
      toast.error(errorList[0], {
        position: 'top-right',
        className: 'bg-warning text-black',
      });
      return;
    }
    if (!crudeId) {
      toast.error('Draft not created — please complete Crude Info first');
      return;
    }

    // Promote any staged Tab-3 files to staging (D4) before the JSON Submit —
    // Draft/Submit (D6) carries no files, it only finalizes what's already
    // staged. On partial failure, abort so the user can retry the pending files.
    if (!(await flushPendingDocuments())) return;

    setSubmitting(true);
    try {
      await submitDraft({
        crudeId,
        masaBerlaku: form.masaBerlaku
          ? formatForAPI(toFirstOfMonth(form.masaBerlaku))
          : null,
        notes: form.evaluationNote || null,
        description: form.implementationNote || null,
        implementations: buildImplementations(form),
      });
      toast.success('Registration submitted for review');
      setTimeout(() => navigate('/crude/registration'), 1200);
    } catch (error) {
      toast.error(`Failed to submit: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  const isTabDone = tabId => tabId < activeTab;

  return (
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb title='Registration' pageTitle='CRUDE' />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Nav tabs className='nav-tabs-custom nav-primary mb-3'>
                  {TAB_CONFIG.map(tab => (
                    <NavItem key={tab.id}>
                      <NavLink
                        className={classnames({
                          active: activeTab === tab.id,
                          done: isTabDone(tab.id),
                        })}
                        // Tabs are not directly clickable forward — progression
                        // is gated by Next (which persists the draft). Allow
                        // clicking back to already-visited tabs.
                        onClick={() => {
                          if (tab.id < activeTab) goTo(tab.id);
                        }}
                        style={{
                          cursor: tab.id < activeTab ? 'pointer' : 'default',
                        }}
                      >
                        <i className={`${tab.icon} align-middle me-1`}></i>
                        <span className='fw-semibold'>{tab.label}</span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId={1}>
                    {activeTab === 1 && (
                      <CrudeInfoTab
                        form={form}
                        setForm={setForm}
                        errors={errors}
                        onNext={handleNextFromCrudeInfo}
                        saving={savingDraft}
                      />
                    )}
                  </TabPane>
                  <TabPane tabId={2}>
                    {activeTab === 2 && (
                      <EvaluationTab
                        form={form}
                        setForm={setForm}
                        documentsByEval={documentsByEval}
                        onAddDocument={handleAddDocument}
                        onDeleteDocument={handleDeleteDocument}
                        onNext={handleNextFromEvaluation}
                        onBack={() => goTo(1)}
                        savingDoc={savingDoc}
                        flushing={flushing}
                      />
                    )}
                  </TabPane>
                  <TabPane tabId={3}>
                    {activeTab === 3 && (
                      <ImplementationTab
                        form={form}
                        setForm={setForm}
                        documentsByEval={documentsByEval}
                        onAddDocument={handleAddDocument}
                        onDeleteDocument={handleDeleteDocument}
                        onSubmit={handleSubmit}
                        onBack={() => goTo(2)}
                        savingDoc={savingDoc}
                        flushing={flushing}
                        submitting={submitting}
                        implErrors={implErrors}
                        setImplErrors={setImplErrors}
                      />
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default RegistrationCreateView;
